import React, { useState, useRef, useEffect, useContext } from "react";

import Section from "../../baseComponents/Section";
import SLEntriesGrid, { GridLoadingStatus } from "../SLEntriesGrid/SLEntriesGrid";
import SLDashboardTiles, { ISLDashboardTileRef } from "../SLDashboardTiles/SLDashboardTiles";

import PageHeading from "../../baseComponents/PageHeading";
import { getDashboard } from "./SLDashboardRequests";
import { ISLEntryGridItem } from "../../schemas/SLEntryGridSchemas";
import NotificationMessages, { INotificationMessagesRef } from "../NotificationMessages";
import { userTypeIndex } from "../SLDashboardTiles/SLDashboardTilesRequests";
import { ActionId } from "../SLDashboardTiles/SLDashboardTiles";
import { SLEntriesError } from "../../services/ErrorHelpers";
import SLActionsManager, { ISLActionsManager } from "../SLActionsManager/SLActionsManager";
import SLBatchPopupOverlay, { ISLBatchPopupOverlay } from "../SLBatchPopup/SLBatchPopup";
import { UserContext } from "../../contexts/UserContext";
import { delay } from "../../utilities/utilities";
import { GridCommandToggle } from "../SLEntriesGrid/utilities";

enum Tiles {
  AuthorMyDrafts = "AuthorMyDrafts",
  AuthorSendToPoster = "AuthorSendToPoster",
  RequestorSignOffRequested = "RequestorSignOffRequested",
  RequestorMyPendingClarifications = "RequestorMyPendingClarifications",
  RequestorReadyToPost = "RequestorReadyToPost",
  ReviewerSignOffPending = "ReviewerSignOffPending",
  ReviewerAwaitingClarifications = "ReviewerAwaitingClarifications"
}

function tilesToSendToAPI(entryType: string): string {
  const tileNametoApiName = {
    "My Draft's": Tiles.AuthorMyDrafts,
    "Send To Poster": Tiles.AuthorSendToPoster,
    "Sign Off Requested": Tiles.RequestorSignOffRequested,
    "My Pending Clarifications": Tiles.RequestorMyPendingClarifications,
    "Ready To Post": Tiles.RequestorReadyToPost,
    "Sign Off Pending": Tiles.ReviewerSignOffPending,
    "Awaiting Clarifications": Tiles.ReviewerAwaitingClarifications
  };
  if (entryType in tileNametoApiName) {
    return tileNametoApiName[entryType as keyof typeof tileNametoApiName];
  }
  return "All";
}

interface ICommandFlags {
  postDisabled: boolean;
  addPostersDisabled: boolean;
  exportEntriesDisabled: boolean;
  sendToPosterDisabled: boolean;
  approveEntriesDisabled: boolean;
  needsClarificationDisabled: boolean;
  recallEntriesDisabled: boolean;
  deleteDraftsDisabled: boolean;
  addAttachmentDisabled: boolean;
}

const commandBarButtonsInitialState: ICommandFlags = {
  postDisabled: true,
  addPostersDisabled: true,
  exportEntriesDisabled: true,
  sendToPosterDisabled: true,
  approveEntriesDisabled: true,
  needsClarificationDisabled: true,
  recallEntriesDisabled: true,
  deleteDraftsDisabled: true,
  addAttachmentDisabled: true
};

interface ICurrentTileActionLinksState {
  currentTile: string;
  commandFlags: ICommandFlags;
}
const initialCurrnetTileAndActionLinks: ICurrentTileActionLinksState = {
  currentTile: "",
  commandFlags: commandBarButtonsInitialState
};

const actionIdsToCommandFlags = (names: ActionId[]) => {
  const newState = { ...commandBarButtonsInitialState };
  for (const name of names) {
    switch (name) {
      case ActionId.SendToPoster:
        newState.sendToPosterDisabled = false;
        break;
      case ActionId.Attachments:
        newState.addAttachmentDisabled = false;
        break;
      case ActionId.Delete:
        newState.deleteDraftsDisabled = false;
        break;
      case ActionId.Recall:
        newState.recallEntriesDisabled = false;
        break;
      case ActionId.SubmitForReview:
        newState.postDisabled = false;
        break;
      case ActionId.AddPosters:
        newState.addPostersDisabled = false;
        break;
      case ActionId.ApproveSLEntries:
        newState.approveEntriesDisabled = false;
        break;
      case ActionId.NeedsClarification:
        newState.needsClarificationDisabled = false;
        break;
      case ActionId.ExportToExcel:
        newState.exportEntriesDisabled = false;
        break;
    }
  }
  return newState;
};

enum DashboardStatus {
  idle = "idle",
  pending = "pending",
  resolved = "resolved",
  rejected = "rejected"
}

const toToggle = (isDisabled: boolean) => {
  return isDisabled ? GridCommandToggle.Disabled : GridCommandToggle.Enabled;
};

const SLDashboard = (): JSX.Element => {
  const { accessToken } = useContext(UserContext);

  const [dashboardGridStatus, setDashboardGridStatus] = useState<DashboardStatus>(DashboardStatus.idle);
  const [gridItems, setGridItems] = useState<ISLEntryGridItem[]>([]);
  const [currentTileActionLinks, setCurrentTileActionLinks] = useState<ICurrentTileActionLinksState>(
    initialCurrnetTileAndActionLinks
  );
  const tilesRef = useRef<ISLDashboardTileRef>(null);
  const slActionsManagerRef = useRef<ISLActionsManager["ref"]>(null);
  const slBatchPopupRef = useRef<ISLBatchPopupOverlay["ref"]>(null);
  const _notificationsRef = useRef<INotificationMessagesRef>(null);

  const onNotification: INotificationMessagesRef["setMessage"] = async (type, messages) => {
    if (_notificationsRef.current) {
      await resetNotifications();
      _notificationsRef.current.setMessage(type, messages);
    }
  };

  const resetNotifications: INotificationMessagesRef["reset"] = async () => {
    if (_notificationsRef.current) {
      _notificationsRef.current.reset();
      await delay(200);
    }
  };

  useEffect(() => {
    setDashboardGridStatus(DashboardStatus.resolved);
  }, [gridItems]);

  useEffect(() => {
    let mounted = true;
    setDashboardGridStatus(DashboardStatus.pending);
    const getGrid = async () => {
      const apiName = tilesToSendToAPI(currentTileActionLinks.currentTile);
      try {
        const items = await getDashboard(apiName, accessToken);
        if (mounted) {
          setGridItems(items);
        }
      } catch (err) {
        if (err instanceof SLEntriesError) {
          onNotification("errors", err.messages);
        }
      }
    };
    if (currentTileActionLinks.currentTile) {
      getGrid();
    }
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTileActionLinks]);

  const loadGrid = async (tileName: userTypeIndex | "", names: ActionId[], refreshState: boolean) => {
    if (tileName === "") {
      setDashboardGridStatus(DashboardStatus.idle);
    } else {
      if (currentTileActionLinks.currentTile !== tileName || refreshState) {
        resetNotifications();
      }
      setCurrentTileActionLinks({
        currentTile: tileName,
        commandFlags: actionIdsToCommandFlags(names)
      });
    }
  };

  const onActionCallback = () => {
    if (tilesRef !== null && tilesRef.current !== null) {
      tilesRef.current.refreshTiles(currentTileActionLinks.currentTile as userTypeIndex);
    }
  };

  return (
    <React.Fragment>
      <SLBatchPopupOverlay ref={slBatchPopupRef} />
      <SLActionsManager
        ref={slActionsManagerRef}
        onNotifications={onNotification}
        batchPopup={slBatchPopupRef.current}
      />
      <div className="content">
        <PageHeading>Subledger Dashboard</PageHeading>
        <NotificationMessages ref={_notificationsRef} />
        <Section label="SL Entries">
          <SLDashboardTiles ref={tilesRef} switchGridTileFn={loadGrid} notificationsFn={onNotification} />
          <SLEntriesGrid
            items={gridItems}
            loading={GridLoadingStatus[dashboardGridStatus]}
            batchPopup={slBatchPopupRef.current}
            actionsManager={slActionsManagerRef.current}
            notificationsFn={onNotification}
            onActionCallback={onActionCallback}
            postDisabled={toToggle(currentTileActionLinks.commandFlags.postDisabled)}
            addPostersDisabled={toToggle(currentTileActionLinks.commandFlags.addPostersDisabled)}
            exportEntriesDisabled={toToggle(currentTileActionLinks.commandFlags.exportEntriesDisabled)}
            sendToPosterDisabled={toToggle(currentTileActionLinks.commandFlags.sendToPosterDisabled)}
            approveEntriesDisabled={toToggle(currentTileActionLinks.commandFlags.approveEntriesDisabled)}
            needsClarificationDisabled={toToggle(currentTileActionLinks.commandFlags.needsClarificationDisabled)}
            recallEntriesDisabled={toToggle(currentTileActionLinks.commandFlags.recallEntriesDisabled)}
            deleteDraftsDisabled={toToggle(currentTileActionLinks.commandFlags.deleteDraftsDisabled)}
            addAttachmentDisabled={toToggle(currentTileActionLinks.commandFlags.addAttachmentDisabled)}
            deleteAttachmentsDisabled={toToggle(currentTileActionLinks.commandFlags.addAttachmentDisabled)}
            useWeightageCalculator={false}
            routeState={() => ({ origin: "/sldashboard", state: { tileName: currentTileActionLinks.currentTile } })}
          />
        </Section>
      </div>
    </React.Fragment>
  );
};

SLDashboard.displayName = "SLDashboard";

export default SLDashboard;
